import React, { useState } from 'react';
import { useCalculator } from '../context/CalculatorContext';
import { formatCurrency, convertFromMXN } from '../utils/formatters';
import Slider from './ui/Slider';
import ChartSeriesToggleCard from './ui/ChartSeriesToggleCard';
import { ChevronDown, ChevronUp, Calculator, Settings, DollarSign, Target, Percent, Plus, Minus, TrendingUp, RefreshCw, CreditCard } from 'lucide-react';

const CalculatorForm: React.FC = () => {
  const {
    investment,
    updateInvestment,
    toggleReinvest,
    toggleContributions,
    toggleLemonPrice,
    toggleTaxes,
    togglePartialCashOut,
    updateCashOutPercentage,
    resetCalculator,
    setUtilityScenario
  } = useCalculator();

  // Handlers for increment/decrement buttons
  const handleIncrementCertificates = () => {
    if (investment.initialCertificates < 20) {
      updateInvestment({ initialCertificates: investment.initialCertificates + 1 });
    }
  };

  const handleDecrementCertificates = () => {
    if (investment.initialCertificates > 1) {
      updateInvestment({ initialCertificates: investment.initialCertificates - 1 });
    }
  };

  const handleIncrementPrice = () => {
    if (investment.certificateBasePrice < 300000) {
      updateInvestment({ certificateBasePrice: investment.certificateBasePrice + 500 });
    }
  };

  const handleDecrementPrice = () => {
    if (investment.certificateBasePrice > 200000) {
      updateInvestment({ certificateBasePrice: investment.certificateBasePrice - 500 });
    }
  };

  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const [isConfigExpanded, setIsConfigExpanded] = useState<string | null>(null);

  return (
    <div className="flex flex-col gap-5">
      {/* Initial Data Section */}
      <button
        onClick={() => setExpandedSection(expandedSection === 'initial' ? null : 'initial')}
        className="w-full flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-3">
          <DollarSign className="w-5 h-5 text-green-600" />
          <span className="font-medium text-gray-700">Datos Iniciales</span>
        </div>
        {expandedSection === 'initial' ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
      </button>

      {expandedSection === 'initial' && (
        <div className="space-y-6 p-4 bg-gray-50 rounded-lg animate-fadeIn">
          {/* Currency Selector */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Moneda</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                className={`py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                  investment.currencyFormat === 'MXN'
                    ? 'bg-green-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                }`}
                onClick={() => updateInvestment({ currencyFormat: 'MXN' })}
              >
                MXN
              </button>
              <button
                className={`py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                  investment.currencyFormat === 'USD'
                    ? 'bg-green-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                }`}
                onClick={() => updateInvestment({ currencyFormat: 'USD' })}
              >
                USD
              </button>
            </div>
          </div>

          {/* Exchange Rate */}
          {investment.currencyFormat === 'USD' && (
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Tipo de Cambio (MXN/USD)</label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
                <input
                  type="number"
                  min="1"
                  step="0.1"
                  value={investment.exchangeRate}
                  onChange={(e) => updateInvestment({ exchangeRate: parseFloat(e.target.value) })}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>
            </div>
          )}

          {/* Initial Certificates */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Certificados Iniciales</label>
            <div className="flex items-center gap-2">
              <button
                onClick={handleDecrementCertificates}
                className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                disabled={investment.initialCertificates <= 1}
              >
                <Minus className="w-4 h-4 text-gray-700" />
              </button>
              <input
                type="number"
                min="1"
                max="100"
                step="1"
                value={investment.initialCertificates}
                onChange={(e) => {
                  const value = parseInt(e.target.value);
                  if (!isNaN(value) && value >= 1 && value <= 100) {
                    updateInvestment({ initialCertificates: value });
                  }
                }}
                className="w-full text-center py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
              <button
                onClick={handleIncrementCertificates}
                className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                disabled={investment.initialCertificates >= 100}
              >
                <Plus className="w-4 h-4 text-gray-700" />
              </button>
            </div>
            <input
              type="range"
              min="1"
              max="100"
              step="1"
              value={investment.initialCertificates}
              onChange={(e) => updateInvestment({ initialCertificates: parseInt(e.target.value) })}
              className="w-full h-2 bg-gradient-to-r from-green-200 to-green-300 rounded-lg appearance-none cursor-pointer"
            />
          </div>

          {/* Certificate Base Price */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Precio Base del Certificado</label>
            <div className="flex items-center gap-2">
              <button
                onClick={handleDecrementPrice}
                className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                disabled={investment.certificateBasePrice <= 200000}
              >
                <Minus className="w-4 h-4 text-gray-700" />
              </button>
              <div className="relative flex-1">
                <DollarSign className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
                <input
                  type="number"
                  min="200000"
                  max="300000"
                  step="500"
                  value={investment.certificateBasePrice}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value);
                    if (!isNaN(value) && value >= 200000 && value <= 300000) {
                      updateInvestment({ certificateBasePrice: value });
                    }
                  }}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  title="Precio base del certificado"
                />
              </div>
              <button
                onClick={handleIncrementPrice}
                className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                disabled={investment.certificateBasePrice >= 300000}
              >
                <Plus className="w-4 h-4 text-gray-700" />
              </button>
            </div>
            <p className="text-xs text-gray-500">
              Precio base del certificado (entre 200,000 y 300,000)
            </p>
          </div>

          {/* Investment Years */}
          <Slider
            label="Horizonte de Inversión"
            value={investment.years}
            min={5}
            max={30}
            step={1}
            onChange={(value) => updateInvestment({ years: value })}
            formatter={(value) => `${value} años`}
          />

          {/* Annual Profit */}
          <div className="space-y-4">

            <label className="text-sm font-medium text-gray-700">Parámetros de Producción</label>
            
            {/* Production per Hectare */}
            <div className="space-y-2">
              <label className="text-xs text-gray-600">Producción Promedio por Hectárea</label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="15000"
                  max="35000"
                  step="500"
                  value={investment.averageProductionPerHectare || 24500}
                  onChange={(e) => {
                    const value = parseInt(e.target.value);
                    if (!isNaN(value) && value >= 15000 && value <= 35000) {
                      updateInvestment({ averageProductionPerHectare: value });
                    }
                  }}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
                <span className="text-gray-700 text-sm">kg/ha</span>
              </div>
            </div>
            
            {/* Price per Kg */}
            <div className="space-y-2">
              <label className="text-xs text-gray-600">Precio Promedio de Venta por Kg</label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="20"
                  max="50"
                  step="1"
                  value={investment.averageSalePricePerKg || 30}
                  onChange={(e) => {
                    const value = parseInt(e.target.value);
                    if (!isNaN(value) && value >= 20 && value <= 50) {
                      updateInvestment({ averageSalePricePerKg: value });
                    }
                  }}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
                <span className="text-gray-700 text-sm">MXN/kg</span>
              </div>
            </div>
            
            {/* Calculated Annual Profit Display */}
            <div className="p-3 bg-green-50 rounded-lg">
              <div className="text-xs text-gray-600 mb-1">Utilidad anual calculada:</div>
              <div className="text-lg font-bold text-green-700">
                {(((investment.averageProductionPerHectare || 24500) * (investment.averageSalePricePerKg || 30) * 0.1 * 0.65 / investment.certificateBasePrice) * 100).toFixed(1)}%
              </div>
              <div className="text-xs text-green-600">
                {(investment.averageProductionPerHectare || 24500).toLocaleString()} kg/ha × ${investment.averageSalePricePerKg || 30}/kg × 0.1 × 0.65
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2">
              <button
                onClick={() => updateInvestment({ 
                  averageProductionPerHectare: 25000,
                  averageSalePricePerKg: 30
                })}
                className={`py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                  (investment.averageProductionPerHectare || 24500) === 25000 && (investment.averageSalePricePerKg || 30) === 30
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Conservador
              </button>
              <button
                onClick={() => updateInvestment({ 
                  averageProductionPerHectare: 30000,
                  averageSalePricePerKg: 35
                })}
                className={`py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                  (investment.averageProductionPerHectare || 24500) === 30000 && (investment.averageSalePricePerKg || 30) === 35
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Moderado
              </button>
              <button
                onClick={() => updateInvestment({ 
                  averageProductionPerHectare: 35000,
                  averageSalePricePerKg: 40
                })}
                className={`py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                  (investment.averageProductionPerHectare || 24500) === 35000 && (investment.averageSalePricePerKg || 30) === 40
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Optimista
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Scenarios Section */}
      <button
        onClick={() => setExpandedSection(expandedSection === 'scenarios' ? null : 'scenarios')}
        className="w-full flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-3">
          <Target className="w-5 h-5 text-green-600" />
          <span className="font-medium text-gray-700">Escenarios</span>
        </div>
        {expandedSection === 'scenarios' ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
      </button>

      {expandedSection === 'scenarios' && (
        <div className="space-y-4 p-4 bg-gray-50 rounded-lg animate-fadeIn">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ChartSeriesToggleCard
              label="Reinvertir Utilidades"
              icon={RefreshCw}
              color="#16a34a"
              checked={investment.reinvestProfits}
              onClick={toggleReinvest}
            />
            
            <ChartSeriesToggleCard
              label="Incremento Precio Limón"
              icon={TrendingUp}
              color="#f59e0b"
              checked={investment.increaseLemonPrice}
              onClick={toggleLemonPrice}
            />

            <ChartSeriesToggleCard
              label="Aplicar Impuestos"
              icon={DollarSign}
              color="#dc2626"
              checked={investment.applyTaxes}
              onClick={toggleTaxes}
            />

            <ChartSeriesToggleCard
              label="Aportaciones Adicionales"
              icon={Plus}
              color="#0284c7"
              checked={investment.additionalContributions}
              onClick={toggleContributions}
            />
            
            <ChartSeriesToggleCard
              label="Boost de Pago"
              icon={RefreshCw}
              color="#8b5cf6"
              checked={investment.enablePaymentBoost || false}
              onClick={() => updateInvestment({ enablePaymentBoost: !investment.enablePaymentBoost })}
            />
          </div>

          {investment.reinvestProfits && (
            <ChartSeriesToggleCard
              label="Retiro Parcial de Utilidades"
              icon={Percent}
              color="#16a34a"
              checked={investment.partialCashOut}
              onClick={togglePartialCashOut}
            />
          )}

          {investment.applyTaxes && (
            <Slider
              label="Tasa de Impuestos"
              value={investment.taxRate}
              min={0}
              max={50}
              step={1}
              onChange={(value) => updateInvestment({ taxRate: value })}
              formatter={(value) => `${value}%`}
            />
          )}

          {investment.additionalContributions && (
            <Slider
              label="Aportación Mensual"
              value={Math.round(convertFromMXN(investment.monthlyContribution, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR))}
              min={5000}
              max={50000}
              step={1000}
              onChange={(value) => updateInvestment({ monthlyContribution: value })}
              formatter={(value) => formatCurrency(value, investment.currencyFormat)}
            />
          )}

          {investment.increaseLemonPrice && (
            <Slider
              label="Incremento Anual Precio Limón"
              value={investment.lemonPriceIncrease}
              min={1}
              max={10}
              step={0.5}
              onChange={(value) => updateInvestment({ lemonPriceIncrease: value })}
              formatter={(value) => `${value}%`}
            />
          )}

          {investment.reinvestProfits && investment.partialCashOut && (
            <Slider
              label="Porcentaje de Retiro"
              value={investment.cashOutPercentage}
              min={0}
              max={100}
              step={1}
              onChange={(value) => updateCashOutPercentage(value)}
              formatter={(value) => `${value}%`}
            />
          )}
          
          {(investment.enablePaymentBoost || false) && (
            <div className="p-4 bg-purple-50 rounded-lg">
              <h4 className="font-medium text-purple-800 mb-2">Boost de Pago Activado</h4>
              <p className="text-sm text-purple-700 mb-2">
                Una vez que los certificados iniciales estén 100% pagados, continuarás aportando el equivalente 
                a la mensualidad original para acelerar la adquisición de nuevos certificados.
              </p>
              <div className="bg-white p-3 rounded-lg">
                <div className="text-xs text-purple-600 mb-1">Mensualidad original calculada:</div>
                <div className="text-lg font-bold text-purple-700">
                  {formatCurrency(
                      (investment.initialCertificates * investment.certificateBasePrice * 0.7) / 48,
                    investment.currencyFormat
                  )}
                </div>
                <div className="text-xs text-purple-600">
                  mensuales ({formatCurrency(
                      (investment.initialCertificates * investment.certificateBasePrice * 0.7) / 4,
                    investment.currencyFormat
                  )} anuales)
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Advanced Settings */}
      <button
        onClick={() => setExpandedSection(expandedSection === 'advanced' ? null : 'advanced')}
        className="w-full flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-3">
          <Settings className="w-5 h-5 text-green-600" />
          <span className="font-medium text-gray-700">Configuración Avanzada</span>
        </div>
        {expandedSection === 'advanced' ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
      </button>

      {expandedSection === 'advanced' && (
        <div className="space-y-4 p-4 bg-gray-50 rounded-lg animate-fadeIn">
          <Slider
            label="Tasa de Apreciación (Años 1-5)"
            value={investment.appreciationRate}
            min={0}
            max={15}
            step={0.5}
            onChange={(value) => updateInvestment({ appreciationRate: value })}
            formatter={(value) => `${value}%`}
          />
          
        </div>
      )}

      {/* Reset Button */}
      <button
        onClick={resetCalculator}
        className="w-full mt-6 py-3 px-4 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
      >
        <Settings className="w-4 h-4" />
        <span>Restablecer Valores</span>
      </button>
    </div>
  );
};

export default CalculatorForm;